import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import Sidebar from './components/Sidebar';
import AuthErrorBoundary from './components/AuthErrorBoundary';
import { UserProvider } from './contexts/UserContext';
import Dashboard from './pages/Dashboard';
import SystemUsers from './pages/SystemUsers';
import Students from './pages/Students';
import Courses from './pages/Courses';
import Books from './pages/Books';
import UnitCodes from './pages/UnitCodes';
import AddStudent from './pages/AddStudent';
import StudentDetails from './pages/StudentDetails';
import AddCourse from './pages/AddCourse';
import CourseDetail from './pages/CourseDetail';
import Codes from './pages/Codes';
import GenerateCode from './pages/GenerateCode';
import Orders from './pages/Orders';
import PromoCodes from './pages/PromoCodes';
import Schedule from './pages/Schedule';
import ContactUs from './pages/ContactUs';
import Login from './pages/login';
import LessonView from './pages/LessonView';
import Subjects from './pages/Subjects';
import SubjectDetails from './pages/SubjectDetails';
import Grades from './pages/Grades';
import Teachers from './pages/Teachers';
import TeacherDetails from './pages/TeacherDetails';
import QuestionBank from './pages/QuestionBank';
import RandomQuiz from './pages/RandomQuiz';
import Sessions from './pages/Sessions';
import SessionDetails from './pages/SessionDetails';
import RefreshTokenTestPage from './pages/RefreshTokenTestPage.tsx';
import Assignment from './pages/Assignment';
import { authService } from './utils/api'; // Import authService

// Add a comment to force recompile

// Protected route component
interface ProtectedRouteProps {
  children: any;
}

const ProtectedRoute = ({ children }: ProtectedRouteProps) => {
  const isAuthenticated = authService.isAuthenticated(); // Use authService
  const location = useLocation();

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return children;
};

const App = () => {
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [authLoading, setAuthLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  useEffect(() => {
    // Check authentication status on app initialization
    const checkAuth = () => {
      try {
        const authenticated = authService.isAuthenticated();
        setIsAuthenticated(authenticated);
      } catch (error) {
        console.error('Auth check failed:', error);
        setIsAuthenticated(false);
      } finally {
        setAuthLoading(false);
      }
    };

    // Listen for auth changes
    const handleAuthChange = () => {
      const authenticated = authService.isAuthenticated();
      setIsAuthenticated(authenticated);
    };

    authService.addAuthChangeListener(handleAuthChange);
    checkAuth();

    return () => {
      authService.removeAuthChangeListener(handleAuthChange);
    };
  }, []);

  // Show loading spinner during auth check
  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <AuthErrorBoundary>
      <UserProvider>
        <Router>
          <Routes>
        <Route path="/login" element={
          isAuthenticated ? <Navigate to="/" /> : <Login />
        } />
        
        <Route path="/*" element={
          <ProtectedRoute children={
            <div className="flex h-screen bg-gray-100">
              {/* Mobile menu button - adjust positioning for right sidebar */}
              <button
                className="lg:hidden fixed top-4 right-4 z-50 p-2 rounded-md bg-primary text-white"
                onClick={() => setIsSidebarOpen(!isSidebarOpen)}
              >
                <svg
                  className="h-6 w-6"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M4 6h16M4 12h16M4 18h16"
                  />
                </svg>
              </button>

              {/* Sidebar - positioned on the right */}
              <div className={`
                fixed inset-y-0 right-0 z-40 lg:relative lg:inset-auto
                ${isSidebarOpen ? 'translate-x-0' : 'translate-x-full lg:translate-x-0'}
                transition-transform duration-300 ease-in-out
              `}>
                <Sidebar onClose={() => setIsSidebarOpen(false)} />
              </div>

              {/* Backdrop */}
              {isSidebarOpen && (
                <div
                  className="fixed inset-0 bg-black bg-opacity-50 z-30 lg:hidden"
                  onClick={() => setIsSidebarOpen(false)}
                />
              )}

              {/* Main content - add proper padding for mobile */}
              <div className="flex-1 overflow-auto w-full custom-pt lg:pt-0 custom-px lg:px-0">
                <Routes>
                  <Route path="/" element={<Dashboard />} />
                  <Route path="/system-users" element={<SystemUsers />} />
                  <Route path="/students" element={<Students />} />
                  <Route path="/students/add" element={<AddStudent />} />
                  <Route path="/students/:id" element={<StudentDetails />} />
                  <Route path="/courses" element={<Courses />} />
                  <Route path="/courses/add" element={<AddCourse />} />
                  <Route path="/courses/:courseId" element={<CourseDetail />} />
                  <Route path="/courses/:courseId/lessons/:lessonId" element={<LessonView />} />
                  <Route path="/books" element={<Books />} />
                  <Route path="/unit-codes" element={<UnitCodes />} />
                  <Route path="/codes" element={<Codes />} />
                  <Route path="/codes/generate" element={<GenerateCode />} />
                  <Route path="/orders" element={<Orders />} />
                  <Route path="/promo-codes" element={<PromoCodes />} />
                  <Route path="/schedule" element={<Schedule />} />
                  <Route path="/contact-us" element={<ContactUs />} />
                  <Route path="/refresh-token-testpage" element={<RefreshTokenTestPage />} />
                  <Route path="/subjects" element={<Subjects />} />
                  <Route path="/subjects/:id" element={<SubjectDetails />} />
                  <Route path="/grades" element={<Grades />} />
                  <Route path="/teachers" element={<Teachers />} />
                  <Route path="/teachers/:id" element={<TeacherDetails />} />
                  <Route path="/question-bank" element={<QuestionBank />} />
                  <Route path="/random-quiz/:courseId/:unitId?" element={<RandomQuiz />} />
                  <Route path="/assignment/:courseId/:unitId?" element={<Assignment />} />
                  <Route path="/sessions" element={<Sessions />} />
                  <Route path="/sessions/:id" element={<SessionDetails />} />
                </Routes>
              </div>
            </div>
          } />
        } />
          </Routes>
        </Router>
      </UserProvider>
    </AuthErrorBoundary>
  );
};

export default App;
